import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import BrandLogo from '@/components/BrandLogo';

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const inviteOrg = searchParams.get('org');
  const inviteRole = searchParams.get('role');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(inviteRole || 'client');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(email, password, name, role, inviteOrg || undefined);
      toast.success('Account created!');
      navigate(user.role === 'admin' ? '/admin' : user.role === 'driver' ? '/driver' : '/client');
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <Button variant="ghost" className="text-muted-foreground hover:text-foreground mb-8 -ml-2" onClick={() => navigate('/')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <Card className="bg-card border-border rounded-2xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <BrandLogo size="lg" />
            </div>
            <CardTitle className="text-foreground text-lg font-medium">Create your account</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-muted-foreground text-xs">Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} required
                  className="bg-muted border-input text-foreground rounded-xl h-11 mt-1 focus:border-brand-accent"
                  placeholder="Your name or company" data-testid="register-name-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="bg-muted border-input text-foreground rounded-xl h-11 mt-1 focus:border-brand-accent"
                  placeholder="you@company.com" data-testid="register-email-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  className="bg-muted border-input text-foreground rounded-xl h-11 mt-1 focus:border-brand-accent"
                  placeholder="Min 6 characters" data-testid="register-password-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="bg-muted border-input text-foreground rounded-xl h-11 mt-1" data-testid="register-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="admin" className="text-foreground">Admin - Fleet Manager</SelectItem>
                    <SelectItem value="driver" className="text-foreground">Driver - Truck Operator</SelectItem>
                    <SelectItem value="client" className="text-foreground">Client - Shipper</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-semibold rounded-full h-12"
                data-testid="register-submit-btn">
                {loading ? 'Creating...' : 'Create Account'}
              </Button>
            </form>

            <div className="text-center pt-4">
              <p className="text-muted-foreground text-sm">Already have an account? <Link to="/login" className="text-brand-accent hover:underline" data-testid="login-link">Sign in</Link></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
